import { createContext, ReactElement, ReactNode, useContext, useMemo } from 'react'

import { LocalStorageKeys } from '../../../enums/localStorageKeys'
import { useClosableBanner } from '../../../hooks/useClosableBanner'
import { TaxReturn } from '../../../types/taxReturn'
import { useFetchVatDeclarations } from '../hooks/useFetchVatDeclarations'

interface VatDeclarationsContextState {
  closeCorrectionBanner: () => void
  isCorrectionBannerClosed: boolean
  isLoadingVatDeclarations: boolean
  vatDeclarations?: TaxReturn[]
  unsettledCorrections: TaxReturn[]
}

const VatDeclarationsContext = createContext<VatDeclarationsContextState | undefined>(undefined)

interface VatDeclarationsContextProps {
  children?: ReactNode
}

export const VatDeclarationsContextProvider = ({ children }: VatDeclarationsContextProps): ReactElement | null => {
  const { isClosed: isCorrectionBannerClosed, close: closeCorrectionBanner } = useClosableBanner(
    LocalStorageKeys.VatDeclarationsCorrectionBanner,
  )

  const { data: vatDeclarations, isLoading: isLoadingVatDeclarations } = useFetchVatDeclarations()

  const unsettledCorrections = useMemo(
    () => vatDeclarations?.filter((value) => value.correctionNo > 0 && !value.isSettled) || [],
    [vatDeclarations],
  )

  return (
    <VatDeclarationsContext.Provider
      value={{
        closeCorrectionBanner,
        isCorrectionBannerClosed,
        isLoadingVatDeclarations,
        vatDeclarations,
        unsettledCorrections,
      }}
    >
      {children}
    </VatDeclarationsContext.Provider>
  )
}

export const useVatDeclarations = () => {
  const context = useContext(VatDeclarationsContext)

  if (!context) {
    throw new Error('VatDeclarationsContextProvider is missing in the module!')
  }

  return context
}
